import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLang } from '../../platform/lang';
import { geolocation } from '../../platform/geolocation';
import { openDocumentCameraStream } from '../../platform/camera';
import { api, uploadToSignedUrl } from '../../core/api/client';
import { getToken, peekAll } from '../../core/offline/db';
import { reportError } from '../../core/telemetry/report';

/**
 * "CHECK MY PHONE" — a self-test the office can ask any driver to run.
 *
 * Owner, 4 Oct: "a button we can ask the drivers to click and then we get the
 * results in our dashboard … something that will tell us once and for all if
 * the app has all it needs on the device." Reached from Help, or by texting a
 * driver app.jwexpressdelivery.com/check.
 *
 * It exercises the same things a delivery does, in the same way:
 *   1. installed app or browser tab, signed in
 *   2. location permission and a real fix, with its accuracy
 *   3. camera opens
 *   4. storage room, and SAVING A PHOTO TO THE PHONE AND READING IT BACK —
 *      the exact step that fails on some iPhones ("Error preparing Blob/File")
 *   5. the office answers, and how fast
 *   6. UPLOADING that photo the way a delivery does, and how long it takes
 *   7. anything still waiting on the phone to be sent
 *
 * Every test has its own time limit and its own try/catch: one failing test
 * never stops the others, and nothing here can affect a delivery. The test
 * photo is a small grey square, uploaded to the face-photo folder where the
 * cleanup job removes files nothing points to.
 *
 * Results go to the office as one 'diag_selfcheck' report (kept under 500
 * characters). The phone's clock is checked server-side by comparing when the
 * phone says it sent the report with when it arrived.
 */

type Status = 'ok' | 'warn' | 'fail';
interface Result { key: string; label: string; status: Status; text: string; fix?: string }

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

/**
 * How to fix each failure, in the driver's words and for THEIR phone. The point
 * of the screen is that a driver can fix most of this alone, without a call.
 */
function fixFor(key: string, es: boolean): string {
  const ios = /iPhone|iPad|Macintosh/.test(navigator.userAgent);
  const F: Record<string, [string, string, string, string]> = {
    // [iPhone en, iPhone es, Android en, Android es]
    app: [
      'In Safari press Share (square with arrow) → "Add to Home Screen" → Add. Then open the app from the new icon.',
      'En Safari presione Compartir (cuadro con flecha) → "Agregar a inicio" → Agregar. Luego abra la app desde el nuevo ícono.',
      'In Chrome press ⋮ (top right) → "Install app" or "Add to Home screen". Then open it from the new icon.',
      'En Chrome presione ⋮ (arriba a la derecha) → "Instalar app" o "Agregar a pantalla principal". Luego ábrala desde el nuevo ícono.'],
    signin: [
      'Close the app, open it again and sign in with Face ID.',
      'Cierre la app, ábrala otra vez e inicie sesión con Face ID.',
      'Close the app, open it again and sign in with your fingerprint or screen lock.',
      'Cierre la app, ábrala otra vez e inicie sesión con su huella o bloqueo de pantalla.'],
    loc: [
      'Settings → Privacy & Security → Location Services → ON. Then scroll to "Safari Websites" → "While Using the App" and turn ON "Precise Location".',
      'Ajustes → Privacidad y seguridad → Localización → ACTIVADO. Luego busque "Sitios web de Safari" → "Cuando se use la app" y active "Ubicación exacta".',
      'Settings → Location → ON. Then Chrome → ⋮ → Settings → Site settings → Location → Allowed.',
      'Ajustes → Ubicación → ACTIVADO. Luego Chrome → ⋮ → Configuración → Configuración de sitios → Ubicación → Permitido.'],
    cam: [
      'Settings → Apps → Safari → Camera → Allow. Then run the self-test again.',
      'Ajustes → Apps → Safari → Cámara → Permitir. Luego haga la autoprueba otra vez.',
      'Chrome → ⋮ → Settings → Site settings → Camera → Allowed. Then run the self-test again.',
      'Chrome → ⋮ → Configuración → Configuración de sitios → Cámara → Permitido. Luego haga la autoprueba otra vez.'],
    room: [
      'Free up space: Settings → General → iPhone Storage. Delete old videos or apps you do not use.',
      'Libere espacio: Ajustes → General → Almacenamiento del iPhone. Borre videos viejos o apps que no use.',
      'Free up space: Settings → Storage. Delete old videos or apps you do not use.',
      'Libere espacio: Ajustes → Almacenamiento. Borre videos viejos o apps que no use.'],
    net: [
      'Check that Airplane Mode is OFF and Cellular Data is ON (Settings → Cellular). Turn off any VPN or ad-blocker. Then try again.',
      'Revise que el Modo avión esté APAGADO y los Datos celulares ENCENDIDOS (Ajustes → Celular). Apague cualquier VPN o bloqueador de anuncios. Luego intente otra vez.',
      'Check that Airplane mode is OFF and Mobile data is ON. Turn off any VPN or data saver. Then try again.',
      'Revise que el Modo avión esté APAGADO y los Datos móviles ENCENDIDOS. Apague cualquier VPN o ahorro de datos. Luego intente otra vez.'],
    q: [
      'Keep the app open on the screen with signal until they send. Do not delete the app.',
      'Mantenga la app abierta en pantalla con señal hasta que se envíen. No borre la app.',
      'Keep the app open on the screen with signal until they send. Do not uninstall the app.',
      'Mantenga la app abierta en pantalla con señal hasta que se envíen. No desinstale la app.'],
  };
  const f = F[key];
  if (!f) return '';
  return f[(ios ? 0 : 2) + (es ? 1 : 0)] ?? '';
}

function within<T>(ms: number, p: Promise<T>): Promise<T> {
  return Promise.race([p, new Promise<T>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);
}
const errName = (e: unknown) => ((e as Error)?.name && (e as Error).name !== 'Error'
  ? (e as Error).name : (e as Error)?.message ?? String(e)).slice(0, 40);

/** A small grey JPEG made on the phone, so the test needs nothing from outside. */
export async function testPhoto(): Promise<Blob> {
  const c = document.createElement('canvas');
  c.width = 320; c.height = 240;
  const g = c.getContext('2d');
  if (g) { g.fillStyle = '#8a8a8a'; g.fillRect(0, 0, 320, 240); g.fillStyle = '#1F00BE'; g.fillText('JW check', 20, 30); }
  const b = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', 0.8));
  if (!b) throw new Error('no_canvas_blob');
  return b;
}

/** Save a photo to the phone's app storage and read it back — like the delivery queue does. */
export async function storeAndReadBack(blob: Blob): Promise<Blob> {
  const name = 'jw-selfcheck';
  const open = () => new Promise<IDBDatabase>((res, rej) => {
    const r = indexedDB.open(name, 1);
    r.onupgradeneeded = () => r.result.createObjectStore('b');
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
  const db1 = await open();
  await new Promise<void>((res, rej) => {
    const tx = db1.transaction('b', 'readwrite');
    tx.objectStore('b').put(blob, 'photo');
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
    tx.onabort = () => rej(tx.error);
  });
  db1.close();
  const db2 = await open();
  const back = await new Promise<Blob>((res, rej) => {
    const r = db2.transaction('b', 'readonly').objectStore('b').get('photo');
    r.onsuccess = () => (r.result instanceof Blob ? res(r.result) : rej(new Error('not_a_blob')));
    r.onerror = () => rej(r.error);
  });
  // Reading the bytes is the part that fails on affected phones.
  const bytes = await back.arrayBuffer();
  db2.close();
  try { indexedDB.deleteDatabase(name); } catch { /* leave it */ }
  if (bytes.byteLength !== blob.size) throw new Error(`size_${bytes.byteLength}_of_${blob.size}`);
  return back;
}

/** Local calendar day, the key for "has this phone done today's check". */
export function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}
export const DAY_KEY = 'jw.selfcheck.day';

export function SelfCheckScreen({ gate = false, onDone }: { gate?: boolean; onDone?: () => void } = {}) {
  const navigate = useNavigate();
  const es = getLang() === 'es';
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<Result[]>([]);
  const [sent, setSent] = useState(false);

  const run = async () => {
    setRunning(true); setResults([]); setSent(false);
    const out: Result[] = [];
    const add = (r: Result) => {
      if (r.status !== 'ok' && !r.fix) {
        const k = r.key === 'idb' || r.key === 'room' ? 'room' : r.key === 'up' ? 'net' : r.key;
        r.fix = fixFor(k, es) || undefined;
      }
      out.push(r); setResults([...out]);
    };
    const R: Record<string, string | number | null> = {};

    // 1. installed, signed in
    let installed = false;
    try {
      installed = window.matchMedia('(display-mode: standalone)').matches
        || (navigator as unknown as { standalone?: boolean }).standalone === true;
    } catch { /* tab */ }
    const signedIn = Boolean(await getToken().catch(() => null));
    R.inst = installed ? 1 : 0; R.tok = signedIn ? 1 : 0;
    /*
     * An iPhone with Safari's "Request Desktop Website" on says it is a Mac.
     * A real Mac has no touch screen; a phone pretending does (4 Oct: Yeisson
     * Perez, enrolled as "iPhone", reporting as a Mac ever since).
     */
    const desktopMode = /Macintosh/.test(navigator.userAgent) && (navigator.maxTouchPoints ?? 0) > 1;
    R.desk = desktopMode ? 1 : 0;
    R.touch = navigator.maxTouchPoints ?? null;
    if (desktopMode) {
      add({ key: 'desk', label: es ? 'Modo de Safari' : 'Safari mode', status: 'fail',
        text: es ? 'el teléfono está pidiendo la versión de escritorio' : 'the phone is asking for the desktop version',
        fix: es ? 'Ajustes → Apps → Safari → "Solicitar sitio web de escritorio" → apague "Todos los sitios web". Luego cierre la app y ábrala desde el ícono.'
                : 'Settings → Apps → Safari → "Request Desktop Website" → turn OFF "All Websites". Then close the app and open it from the icon.' });
    }
    add({ key: 'app', label: es ? 'Aplicación' : 'App',
      status: installed && signedIn ? 'ok' : 'warn',
      fix: !installed ? fixFor('app', es) : !signedIn ? fixFor('signin', es) : undefined,
      text: (installed ? (es ? 'instalada en la pantalla de inicio' : 'installed on the home screen')
                       : (es ? 'abierta en el navegador — instálela' : 'open in a browser tab — install it'))
        + (signedIn ? '' : es ? ' · sesión cerrada' : ' · signed out') });

    // 2. location
    try {
      const perm = await navigator.permissions?.query({ name: 'geolocation' as PermissionName }).then((p) => p.state).catch(() => null);
      R.gperm = perm ?? null;
      const fix = await within(15_000, geolocation.getFreshFix(12_000));
      R.loc = Math.round(fix.accuracyM);
      add({ key: 'loc', label: es ? 'Ubicación' : 'Location', status: fix.accuracyM <= 100 ? 'ok' : 'warn',
        text: (es ? 'precisión ' : 'accurate to ') + Math.round(fix.accuracyM) + ' m' });
    } catch (e) {
      R.loc = 'x:' + errName(e);
      add({ key: 'loc', label: es ? 'Ubicación' : 'Location', status: 'fail',
        text: es ? 'el teléfono no da la ubicación — active Ubicación para Safari/Chrome'
                 : 'the phone gives no location — turn Location on for Safari/Chrome' });
    }

    // 3. camera
    try {
      const stream = await within(10_000, openDocumentCameraStream());
      stream.getTracks().forEach((t) => t.stop());
      R.cam = 'ok';
      add({ key: 'cam', label: es ? 'Cámara' : 'Camera', status: 'ok', text: es ? 'funciona' : 'works' });
    } catch (e) {
      R.cam = 'x:' + errName(e);
      add({ key: 'cam', label: es ? 'Cámara' : 'Camera', status: 'fail',
        text: es ? 'no se pudo abrir — revise el permiso de cámara' : 'could not open — check the camera permission' });
    }

    // 4. storage room + save a photo and read it back
    try {
      const est = navigator.storage?.estimate ? await navigator.storage.estimate() : null;
      const free = est?.quota ? Math.round(100 * (1 - (est.usage ?? 0) / est.quota)) : null;
      R.free = free; R.quota = est?.quota ? Math.round(est.quota / 1048576) : null;
      // Older phones (e.g. iOS 15) do not report it at all. That is not a fault —
      // the photo save test below is what really matters — so it is shown as fine
      // (owner's own iPhone, 4 Oct: "it says it could not read storage").
      add({ key: 'room', label: es ? 'Espacio' : 'Storage', status: free === null ? 'ok' : free < 10 ? 'fail' : free < 25 ? 'warn' : 'ok',
        text: free === null ? (es ? 'este teléfono no lo informa (normal)' : 'this phone does not report it (normal)')
          : `${free}% ${es ? 'libre' : 'free'}${free < 10 ? (es ? ' — libere espacio en el teléfono' : ' — free up space on the phone') : ''}` });
    } catch (e) { R.free = 'x:' + errName(e); }

    let stored: Blob | null = null;
    try {
      const photo = await testPhoto();
      const t0 = performance.now();
      stored = await within(8_000, storeAndReadBack(photo));
      R.idb = Math.round(performance.now() - t0);
      add({ key: 'idb', label: es ? 'Guardar foto' : 'Saving a photo', status: 'ok', text: es ? 'funciona' : 'works' });
    } catch (e) {
      R.idb = 'x:' + errName(e);
      add({ key: 'idb', label: es ? 'Guardar foto' : 'Saving a photo', status: 'fail',
        text: es ? 'el teléfono no pudo guardar la foto' : 'the phone could not save the photo' });
    }

    // 5. the office answers
    try {
      const t0 = performance.now();
      const res = await within(12_000, fetch(`${API_BASE}/livez`, { cache: 'no-store' }));
      R.net = res.ok ? Math.round(performance.now() - t0) : 'x:' + res.status;
      const ms = Math.round(performance.now() - t0);
      add({ key: 'net', label: es ? 'Conexión con la oficina' : 'Connection to the office',
        status: res.ok ? (ms < 3000 ? 'ok' : 'warn') : 'fail', text: res.ok ? `${ms} ms` : `error ${res.status}` });
    } catch (e) {
      R.net = 'x:' + errName(e);
      add({ key: 'net', label: es ? 'Conexión con la oficina' : 'Connection to the office', status: 'fail',
        text: es ? 'no hay respuesta' : 'no answer' });
    }

    // 6. upload the stored photo the way a delivery does
    try {
      const blob = stored ?? await testPhoto();
      const t0 = performance.now();
      const signed = await within(12_000, api.signIdentity('image/jpeg'));
      await within(45_000, uploadToSignedUrl(signed.uploadUrl, blob, signed.requiredHeaders));
      R.up = Math.round(performance.now() - t0);
      const ms = Math.round(performance.now() - t0);
      add({ key: 'up', label: es ? 'Enviar una foto' : 'Sending a photo', status: ms < 10_000 ? 'ok' : 'warn',
        text: `${(ms / 1000).toFixed(1)} s` });
    } catch (e) {
      R.up = 'x:' + errName(e);
      add({ key: 'up', label: es ? 'Enviar una foto' : 'Sending a photo', status: 'fail',
        text: es ? 'no se pudo enviar' : 'could not send' });
    }

    // 7. anything waiting on the phone
    try {
      const q = await peekAll();
      R.q = q.length;
      R.qmin = q.length ? Math.round((Date.now() - Math.min(...q.map((i) => i.queuedAt))) / 60_000) : 0;
      add({ key: 'q', label: es ? 'Entregas sin enviar' : 'Deliveries waiting to send',
        status: q.length === 0 ? 'ok' : 'warn',
        text: q.length === 0 ? (es ? 'ninguna' : 'none') : `${q.length} (${R.qmin} min)` });
    } catch (e) { R.q = 'x:' + errName(e); }

    R.on = navigator.onLine ? 1 : 0;
    reportError('diag_selfcheck', 'check', JSON.stringify(R).slice(0, 480));
    setSent(true);
    // Today's check is done whatever the result — it is mandatory to RUN, never to pass.
    try { localStorage.setItem(DAY_KEY, todayKey()); } catch { /* storage unusable */ }
    setRunning(false);
  };

  const icon = (s: Status) => (s === 'ok' ? '✅' : s === 'warn' ? '⚠️' : '❌');

  return (
    <main className="screen">
      {!gate && <button className="btn link" onClick={() => navigate('/help')}>&larr; {es ? 'Ayuda' : 'Help'}</button>}
      <h1>{gate ? (es ? 'Antes de su ruta: autoprueba de la app' : 'Before your route: app self-test')
                : (es ? 'Autoprueba de la app' : 'App self-test')}</h1>
      {gate && <p><b>{es ? 'Una vez al día. Es obligatorio y tarda unos 15 segundos.'
                         : 'Once a day. It is required and takes about 15 seconds.'}</b></p>}
      <p className="muted">{es
        ? 'La app se prueba a sí misma para asegurar que tiene todo lo que necesita para funcionar. Tarda unos segundos.'
        : 'The app tests itself to make sure it has everything it needs to work. It takes a few seconds.'}</p>
      <div className="selfcheck-fix">
        <b>{es ? 'Cuando el teléfono pregunte:' : 'When the phone asks:'}</b>
        <ul>
          <li>{es ? 'Ubicación → "Permitir mientras se usa la app" (y deje "Ubicación exacta" ACTIVADA)'
                  : 'Location → "Allow While Using App" (and keep "Precise Location" ON)'}</li>
          <li>{es ? 'Cámara → "Permitir"' : 'Camera → "Allow"'}</li>
        </ul>
        {es ? 'La app necesita las dos para registrar entregas. La oficina recibe este resultado, y la app avisa a la oficina si la ubicación o la cámara se apagan después.'
            : 'The app needs both to record deliveries. The office receives this result, and the app tells the office if location or camera are switched off later.'}
      </div>
      <button className="btn primary" disabled={running} onClick={() => void run()}>
        {running ? (es ? 'Probando…' : 'Testing…') : (es ? 'Empezar la autoprueba' : 'Start the self-test')}
      </button>
      <ul className="selfcheck-list">
        {results.map((r) => (
          <li key={r.key}><span aria-hidden>{icon(r.status)}</span> <b>{r.label}</b> — {r.text}
            {r.fix && <div className="selfcheck-fix">{es ? 'Cómo arreglarlo: ' : 'How to fix: '}{r.fix}</div>}</li>
        ))}
      </ul>
      {sent && (results.every((r) => r.status === 'ok')
        ? <p><b>{es ? '✅ Todo está bien. La oficina ya recibió el resultado.' : '✅ Everything is fine. The office has the results.'}</b></p>
        : <p><b>{es ? 'Arregle lo marcado arriba y presione "Empezar la autoprueba" otra vez. La oficina ya recibió el resultado.'
                     : 'Fix what is marked above, then press "Start the self-test" again. The office has the results.'}</b></p>)}
      {gate && sent && !running && (
        <button className="btn primary" onClick={() => onDone?.()}>
          {es ? 'Continuar a mi ruta →' : 'Continue to my route →'}
        </button>)}
    </main>
  );
}
